import { getGaAttributeValueBetslip } from '~shared/utils/google-analytics';
import { isSelectionSuspended } from './selectionChecks';
import { isCombinationBet } from '~Betslip/uiModes/Full/Components/BetReceipt/utils';

export const formatSelectionsToRetain = (bets) => {
    const selectionFlags = [];
    let anyDuplicateCheck = [];
    const selectionsToRetain = bets.reduce((acc, bet) => {
        /**
         * Combination bets: We only get the DB selection id after the bet has been placed.  It does
         * not exist until after bet placement.
         * The combination selection id needs to be replaced with its underlying selection ids
         */
        const { selection_ids: betSelectionIds, combination_selections } = bet;
        let selectionIds = isCombinationBet(bet)
            ?
            betSelectionIds.reduce((allIds, selectionId) => {
                if (combination_selections?.[selectionId]) {

                    return allIds.concat(combination_selections[selectionId]);
                }

                return allIds.concat([selectionId]);
            }, [])
            :
            betSelectionIds;

        const castMarkets = [
            'Forecast',
            'Tricast',
        ];
        const betLocateValue = getGaAttributeValueBetslip('receipt');
        if (castMarkets.includes(bet.bet_type)) {
            if (bet.is_any) {
                const orderedCombinations = [...bet.combinations].sort();
                if (JSON.stringify(anyDuplicateCheck) === JSON.stringify(orderedCombinations)) {
                    return acc;
                }
                anyDuplicateCheck = orderedCombinations;
            }
            const selectionId = bet.combinations.join('_');

            selectionFlags.push({
                id: selectionId,
                is_cast_market: true,
                is_any: bet.is_any,
                bet_locate: betLocateValue,
            });

            selectionIds = [selectionId];
        } else if (bet.bet_type === 'Scorecast') {
            const selectionId = betSelectionIds.join('_');

            selectionFlags.push({
                id: selectionId,
                is_scorecast: true,
                bet_locate: betLocateValue,
            });

            selectionIds = [selectionId];
        } else if (Array.isArray(betSelectionIds)) {
            betSelectionIds.forEach((id) => {
                selectionFlags.push({
                    id: String(id),
                    bet_locate: betLocateValue,
                });
            });
        }

        return [
            ...acc,
            ...selectionIds,
        ];
    }, []);

    return {
        selectionsToRetain,
        selectionFlags,
    };
};

const ERROR_CODES_SUSPENDED = [
    'SELECTION_SUSPENDED_SINGLE',
    'SELECTION_SUSPENDED_MULTIBET',
];

/**
 * SUSPENDED SELECTIONS
 */

export const formatSuspendedSelectionsToRetain = (existingSuspendedSelections, errors) => {
    const newSuspended = errors
        .filter(({ id }) => ERROR_CODES_SUSPENDED.includes(id))
        .reduce((acc, curr) => {
            const suspendedSelections = curr?.data?.selections || {};

            return {
                ...acc,
                ...suspendedSelections,
            };
        }, {});

    const suspendedToRetain = [
        ...Object.keys(newSuspended),
        ...Object.keys(existingSuspendedSelections),
    ];
    const suspendedToRetainFlags = suspendedToRetain.map(selectionId => ({
        id: selectionId,
        is_scorecast: false,
        is_cast_market: false,
        is_any: false,
        bet_locate: getGaAttributeValueBetslip('suspended-retained'),
    }));

    return {
        suspendedToRetain,
        suspendedToRetainFlags,
    };
};

/**
 * get map suspended_selection_id -> suspension_details
 * @param {Array} errors in this response
 * @returns {Object} map of suspended selections
 */
const getErrorsSuspendedSelections = (errors) => {
    const suspendedErrors = errors.filter(
        error => ERROR_CODES_SUSPENDED.includes(error.id)
    );

    const suspendedSelections = suspendedErrors.reduce(
        (acc, suspendedError) => {
            const {
                data: { selections },
            } = suspendedError;

            return {
                ...acc,
                ...selections,
            };
        },
        {},
    );

    return suspendedSelections;
};

/**
 * Check response from spectate/betslip/bet XHR call for suspended selections and update
 * store
 *
 * @param {Object} payload with bets and errors
 * @param {Object} state in the store
 *
 * @returns {void} nothin: this method has a side-effect (Immer supported mutation of store)
 */
export const suspendSelectionsInStore = (payload, state) => {
    const {
        bets, errors,
    } = payload;
    const suspendedSelections = getErrorsSuspendedSelections(errors);

    const mapBetSelectionIdToCombination = bets.reduce((acc, curr) => {
        if (curr.combinations?.length) {
            const [id] = curr.selection_ids;

            return {
                ...acc,
                [id]: curr.combinations,
            };
        }

        return acc;
    }, {});

    Object.keys(suspendedSelections).forEach((selectionId) => {
        const suspended = isSelectionSuspended(suspendedSelections[selectionId]);

        if (state[selectionId]) {
            state[selectionId] = {
                ...state[selectionId],
                ...suspendedSelections[selectionId],
                suspended,
            };
        } else {
            // it is an fc/tc/sc; use selection-id to get the combination-id
            const combinationId = mapBetSelectionIdToCombination[selectionId].join('_');
            state[combinationId] = {
                ...state[combinationId],
                ...suspendedSelections[selectionId],
                suspended,
            };
        }
    });
};
